package er.prateek.crossly.ui.screens.crossword

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.game.crossword.CrosswordEngine
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game

/**
 * Full Across/Down clue lists — the affordance a crossword on a large screen is
 * expected to have, and the reason the wide layout is worth building: the player
 * reads clues and clicks straight to the word instead of paging one at a time.
 *
 * Solved clues are struck through, and the list scrolls itself to keep the
 * active clue visible as the cursor moves around the grid.
 */
@Composable
fun ClueList(
    engine: CrosswordEngine,
    modifier: Modifier = Modifier,
) {
    val colors = MaterialTheme.game
    val current = engine.currentClue()
    val across = engine.cluesFor(ClueDirection.ACROSS)
    val down = engine.cluesFor(ClueDirection.DOWN)
    val state = rememberLazyListState()

    LaunchedEffect(current, engine.direction) {
        val target = current ?: return@LaunchedEffect
        // +1 / +1 offsets account for the two section headers in the same list.
        val index = if (engine.direction == ClueDirection.ACROSS) {
            across.indexOfClue(target).takeIf { it >= 0 }?.plus(1)
        } else {
            down.indexOfClue(target).takeIf { it >= 0 }?.plus(across.size + 2)
        }
        index?.let { state.animateScrollToItem(it) }
    }

    LazyColumn(
        modifier = modifier,
        state = state,
        verticalArrangement = Arrangement.spacedBy(Spacing.xxs),
    ) {
        listOf(ClueDirection.ACROSS to across, ClueDirection.DOWN to down).forEach { (direction, clues) ->
            item(key = "header-$direction") {
                Text(
                    text = directionLabel(direction).uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    color = colors.inkMuted,
                    modifier = Modifier.padding(top = Spacing.md, bottom = Spacing.xs),
                )
            }
            items(
                count = clues.size,
                key = { index -> "$direction-${clues[index].number}-${clues[index].row}-${clues[index].col}" },
            ) { index ->
                val clue = clues[index]
                ClueRow(
                    clue = clue,
                    isActive = engine.direction == direction && current != null && current.isSameAs(clue),
                    isSolved = engine.isClueSolved(clue, direction),
                    onClick = { engine.selectClue(clue, direction) },
                )
            }
        }
    }
}

@Composable
private fun ClueRow(
    clue: Clue,
    isActive: Boolean,
    isSolved: Boolean,
    onClick: () -> Unit,
) {
    val colors = MaterialTheme.game
    val background by animateColorAsState(
        if (isActive) colors.accent.copy(alpha = 0.14f) else colors.accent.copy(alpha = 0f),
        tween(200),
        label = "clueRowBg",
    )

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(background)
            .clickable(onClick = onClick)
            .padding(horizontal = Spacing.sm, vertical = Spacing.sm),
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        Text(
            text = clue.number.toString(),
            style = MaterialTheme.typography.labelMedium,
            color = if (isActive) colors.accent else colors.inkMuted,
            modifier = Modifier.width(24.dp),
        )
        Text(
            text = clue.clue,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = if (isActive) FontWeight.SemiBold else FontWeight.Normal,
            color = if (isSolved) colors.inkMuted else colors.ink,
            textDecoration = if (isSolved) TextDecoration.LineThrough else null,
            modifier = Modifier.weight(1f),
        )
    }
}

/** Clue numbers repeat across directions, so identity is number + start cell. */
private fun Clue.isSameAs(other: Clue): Boolean =
    number == other.number && row == other.row && col == other.col

private fun List<Clue>.indexOfClue(clue: Clue): Int = indexOfFirst { it.isSameAs(clue) }
